import React, { useMemo, useState } from 'react';
import flowRight from 'lodash.flowright';
import { connect } from 'react-redux';
import MaterialTable, { MTableToolbar } from '@material-table/core';
import { withStyles } from '@material-ui/styles';

import { getExternalNoRanges } from '../actions/getExternalNoRanges';
import withDetailsView from '../../../utils/withDetailsView';
import { mapTableOptions } from '../../../consts/tableOptions';
import useTableLocalization from '../../../utils/useTableLocalization';
import tableColumns from '../components/connectTableColumns';
import useLocalTableStore from '../../../utils/useLocalTableStore';
import { actions as reduxActions } from '../reducers';
import withUserCheck from '../../../utils/withUserCheck';
import {
  doesArrayHasLength,
  isDefined,
  isUndefined
} from '../../../utils/isDefined';
import FilterButton from '../../../partials/tableCustomButtons/FilterButton';
import styles from '../styles';
import customTableIcons from '../../../partials/tableIcons/customTableIcons';
import { setRange } from '../../../modals/editRange/actions/setRange';
import { openRangesMultiedit } from '../../../modals/numberRangeMultiedit/actions/setRanges';
import ConnectRangesDetails from './ConnectRangesDetails';
import { translate } from '../../../i18n/I18nProvider';
import ConnectRangesTableHeader from './ConnectRangesTableHeader';

const ConnectRangesTable = ({
  connectRanges,
  columns,
  setColumns,
  mainSelected,
  classes,
  clientName,
  isConnectRangesTable,
  externalRangeFilter
}) => {
  const [filtering, setFiltering] = useState(false);
  const { localization, language } = useTableLocalization();

  const tableDefaultColumns = useMemo(() => {
    return tableColumns();
  }, []);

  const {
    pageSize,
    onChangeRowsPerPage,
    onChangeColumnHidden
  } = useLocalTableStore({
    tableName: 'ConnectNumberRanges-1',
    setColumns,
    tableDefaultColumns,
    language
  });

  const actions = useMemo(
    () => {
      let buttons = [
        {
          icon: () => <FilterButton />,
          onClick: () => {
            setFiltering(!filtering);
          },
          isFreeAction: true,
          tooltip: translate('BUTTON.FILTER')
        }
      ];

      return buttons;
    },
    // eslint-disable-next-line
    [filtering]
  );

  const options = useMemo(() => {
    return {
      ...mapTableOptions({ tableName: 'numberRanges', clientName }),
      filtering,
      pageSize
    };
  }, [filtering, clientName, pageSize]);

  const detailPanel = [
    rowData => ({
      icon: () => (
        <i
          className="fas fa-chevron-right"
          style={{
            fontSize: '12px'
          }}
        ></i>
      ),
      openIcon: () => (
        <i
          className="fas fa-chevron-down"
          style={{
            fontSize: '12px'
          }}
        ></i>
      ),
      render: () => <ConnectRangesDetails data={rowData} />
    })
  ];

  const dataToShow = useMemo(() => {
    switch (mainSelected) {
      case 'assigned': {
        const assigned = connectRanges.filter(number =>
          doesArrayHasLength(number.assigned)
        );

        return mapDataToShow(assigned, externalRangeFilter, 'assigned');
      }
      case 'unassigned': {
        const unassigned = connectRanges.filter(number =>
          doesArrayHasLength(number.unassigned)
        );

        return mapDataToShow(unassigned, externalRangeFilter, 'unassigned');
      }
      case 'all': {
        return mapDataToShow(connectRanges, externalRangeFilter, 'all');
      }
      default:
        return connectRanges;
    }
    // eslint-disable-next-line
  }, [mainSelected, connectRanges, externalRangeFilter]);

  return (
    <div>
      <MaterialTable
        components={{
          Toolbar: props => (
            <MTableToolbar
              classes={{
                title: classes.toolbarTitle,
                spacer: classes.toolbarSpacer,
                actions: classes.toolbarActions,
                highlight: classes.toolbarHighlight
              }}
              {...props}
            />
          )
        }}
        title={<ConnectRangesTableHeader showSelector={true} />}
        icons={customTableIcons}
        actions={actions}
        options={options}
        columns={columns}
        data={dataToShow}
        localization={localization}
        onChangeColumnHidden={onChangeColumnHidden}
        onRowsPerPageChange={onChangeRowsPerPage}
        detailPanel={detailPanel}
      />
    </div>
  );
};

const mapStatesToProps = ({ externalNoRanges, auth, selectClient, loader }) => {
  return {
    ...externalNoRanges,
    isLoading: loader.isSpinVisible,
    clientName: selectClient.selectedClient.name,
    connectClientId: selectClient.selectedClient.connect30_domain,
    userConnectId: auth.user.connect30_user_id,
    customJobs: auth.customJobs
  };
};

const mapDispatchToProps = {
  getExternalNoRanges,
  setColumns: reduxActions.setColumns,
  resetExternalNoRanges: reduxActions.resetExternalNoRanges,
  setRange,
  openRangesMultiedit
};

export default flowRight(
  withDetailsView,
  withUserCheck,
  connect(mapStatesToProps, mapDispatchToProps),
  withStyles(styles)
)(ConnectRangesTable);

const mapDataToShow = (array, externalRangeFilter, key) => {
  if (externalRangeFilter === 'our') {
    return array.reduce((arr, el) => {
      const numbers = el[key].filter(number => isDefined(number.founded));

      return doesArrayHasLength(numbers)
        ? [...arr, { ...el, key: numbers }]
        : arr;
    }, []);
  }

  if (externalRangeFilter === 'external') {
    return array.reduce((arr, el) => {
      const numbers = el[key].filter(number => isUndefined(number.founded));

      return doesArrayHasLength(numbers)
        ? [...arr, { ...el, key: numbers }]
        : arr;
    }, []);
  }

  return array;
};
